import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';

const SocialLinksManager = ({ setMessage }) => {
    const { apiRequest } = useAdmin();
    const [socialLinks, setSocialLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        loadSocialLinks();
    }, []);

    const loadSocialLinks = async () => {
        try {
            setLoading(true);
            const data = await apiRequest('/social-links/all');

            if (data.success) {
                // Filter out duplicates by platform (keep only first occurrence)
                const uniqueLinks = [];
                const platformSet = new Set();

                data.socialLinks.forEach(link => {
                    if (!platformSet.has(link.platform)) {
                        platformSet.add(link.platform);
                        uniqueLinks.push(link);
                    }
                });

                setSocialLinks(uniqueLinks);

                if (uniqueLinks.length === 0) {
                    setIsInitialized(false);
                    setMessage({ type: 'info', text: 'Social links not found. Click Initialize to create default links.' });
                } else {
                    setIsInitialized(true);
                    setMessage({ type: 'success', text: `Loaded ${uniqueLinks.length} social links` });
                }
            }
        } catch (error) {
            console.error('Failed to load social links:', error);
            setMessage({ type: 'error', text: 'Error loading social links' });
            setIsInitialized(false);
        } finally {
            setLoading(false);
        }
    };

    const initializeSocialLinks = async () => {
        try {
            setSaving(true);
            const data = await apiRequest('/social-links/initialize', 'POST');
            if (data.success) {
                // Filter duplicates
                const uniqueLinks = [];
                const platformSet = new Set();

                data.socialLinks.forEach(link => {
                    if (!platformSet.has(link.platform)) {
                        platformSet.add(link.platform);
                        uniqueLinks.push(link);
                    }
                });

                setSocialLinks(uniqueLinks);
                setIsInitialized(true);
                setMessage({
                    type: 'success',
                    text: data.duplicates
                        ? `Social links initialized! Removed ${data.duplicates.length} duplicates.`
                        : 'Social links initialized successfully!'
                });
            }
        } catch (error) {
            console.error('Failed to initialize social links:', error);
            setMessage({ type: 'error', text: 'Error initializing social links' });
        } finally {
            setSaving(false);
        }
    };

    const handleUrlChange = (id, newUrl) => {
        setSocialLinks(prevLinks =>
            prevLinks.map(link =>
                link._id === id ? { ...link, url: newUrl } : link
            )
        );
    };

    const handleOrderChange = (id, newOrder) => {
        setSocialLinks(prevLinks =>
            prevLinks.map(link =>
                link._id === id ? { ...link, displayOrder: parseInt(newOrder) || 0 } : link
            )
        );
    };

    const handleActiveChange = (id, isActive) => {
        setSocialLinks(prevLinks =>
            prevLinks.map(link =>
                link._id === id ? { ...link, isActive } : link
            )
        );
    };

    const handleCustomIconChange = (id, customIcon) => {
        setSocialLinks(prevLinks =>
            prevLinks.map(link =>
                link._id === id ? { ...link, customIcon } : link
            )
        );
    };

    const handleSaveAll = async () => {
        try {
            setSaving(true);

            // Use bulk update endpoint
            const data = await apiRequest('/social-links/bulk/update', 'PUT', { socialLinks });

            if (data.success) {
                setMessage({
                    type: 'success',
                    text: data.message || 'Social links updated successfully!'
                });
                await loadSocialLinks(); // Reload to get fresh data
            }
        } catch (error) {
            console.error('Failed to update social links:', error);
            setMessage({
                type: 'error',
                text: `Error updating social links: ${error.message}`
            });

            // Fallback: Try individual updates
            try {
                const updatePromises = socialLinks.map(async (link) => {
                    try {
                        const response = await apiRequest(`/social-links/${link._id}`, 'PUT', {
                            url: link.url,
                            customIcon: link.customIcon || '',
                            displayOrder: link.displayOrder || 0,
                            isActive: link.isActive
                        });
                        return response.success;
                    } catch (err) {
                        console.error(`Failed to update link ${link._id}:`, err);
                        return false;
                    }
                });

                const results = await Promise.all(updatePromises);
                const successCount = results.filter(result => result).length;

                if (successCount > 0) {
                    setMessage({
                        type: 'warning',
                        text: `Updated ${successCount} out of ${socialLinks.length} social links using fallback method`
                    });
                    await loadSocialLinks();
                }
            } catch (fallbackError) {
                console.error('Fallback update also failed:', fallbackError);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleResetDefaults = async () => {
        if (!window.confirm('Are you sure you want to reset all social links to defaults? This will reset URLs and settings.')) {
            return;
        }

        try {
            setSaving(true);
            const data = await apiRequest('/social-links/reset', 'POST');
            if (data.success) {
                // Filter duplicates
                const uniqueLinks = [];
                const platformSet = new Set();

                data.socialLinks.forEach(link => {
                    if (!platformSet.has(link.platform)) {
                        platformSet.add(link.platform);
                        uniqueLinks.push(link);
                    }
                });

                setSocialLinks(uniqueLinks);
                setMessage({ type: 'success', text: 'Social links reset to defaults!' });
            }
        } catch (error) {
            console.error('Failed to reset social links:', error);
            setMessage({ type: 'error', text: 'Error resetting social links' });
        } finally {
            setSaving(false);
        }
    };

    const getPlatformDisplayName = (platform) => {
        const platformNames = {
            'facebook': 'Facebook',
            'twitter': 'Twitter / X',
            'instagram': 'Instagram',
            'linkedin': 'LinkedIn',
            'youtube': 'YouTube',
            'tiktok': 'TikTok',
            'pinterest': 'Pinterest',
            'whatsapp': 'WhatsApp',
            'snapchat': 'Snapchat',
            'telegram': 'Telegram'
        };
        return platformNames[platform] || platform.charAt(0).toUpperCase() + platform.slice(1);
    };

    const getPlatformIcon = (platform, customIcon = '') => {
        if (customIcon) return customIcon;

        const platformIcons = {
            'facebook': 'fab fa-facebook-f',
            'twitter': 'fab fa-twitter',
            'instagram': 'fab fa-instagram',
            'linkedin': 'fab fa-linkedin-in',
            'youtube': 'fab fa-youtube',
            'tiktok': 'fab fa-tiktok',
            'pinterest': 'fab fa-pinterest',
            'whatsapp': 'fab fa-whatsapp',
            'snapchat': 'fab fa-snapchat',
            'telegram': 'fab fa-telegram'
        };
        return platformIcons[platform] || 'fas fa-share-alt';
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading social links...</p>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">
                    <i className="fas fa-share-alt text-primary me-2"></i>
                    Social Media Links Management
                </h4>
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-outline-primary"
                        onClick={handleResetDefaults}
                        disabled={saving}
                    >
                        <i className="fas fa-redo me-2"></i>
                        Reset to Defaults
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleSaveAll}
                        disabled={saving || !isInitialized}
                    >
                        {saving ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save me-2"></i>
                                Save All Changes
                            </>
                        )}
                    </button>
                </div>
            </div>

            {!isInitialized ? (
                <div className="card">
                    <div className="card-body text-center py-5">
                        <i className="fas fa-share-alt fa-4x text-muted mb-4"></i>
                        <h3>Social Links Not Initialized</h3>
                        <p className="text-muted mb-4">
                            Social media links have not been set up yet. Click the button below to create default social links.
                        </p>
                        <button
                            className="btn btn-primary btn-lg"
                            onClick={initializeSocialLinks}
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Initializing...
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-plus me-2"></i>
                                    Initialize Social Links
                                </>
                            )}
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="card mb-4">
                        <div className="card-header bg-white">
                            <h5 className="mb-0">Instructions</h5>
                        </div>
                        <div className="card-body">
                            <div className="alert alert-info mb-0">
                                <i className="fas fa-info-circle me-2"></i>
                                <strong>How to use:</strong>
                                <ul className="mb-0 mt-2">
                                    <li>Toggle the switch to activate/deactivate platforms</li>
                                    <li>Enter your profile URLs for each platform</li>
                                    <li>Change the "Order" number to rearrange (lower numbers appear first)</li>
                                    <li>Use custom Font Awesome icon classes if needed</li>
                                    <li>Click "Save All Changes" to apply modifications</li>
                                    <li>Only active platforms will appear on the website</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Social Links List */}
                    <div className="card">
                        <div className="card-header bg-white">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Manage Social Links ({socialLinks.length} platforms)</h5>
                                <span className="badge bg-primary">
                                    Active: {socialLinks.filter(link => link.isActive).length} / {socialLinks.length}
                                </span>
                            </div>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '70px' }}>Order</th>
                                            <th style={{ width: '120px' }}>Icon</th>
                                            <th style={{ width: '150px' }}>Platform</th>
                                            <th>URL</th>
                                            <th style={{ width: '120px' }}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {socialLinks
                                            .sort((a, b) => a.displayOrder - b.displayOrder)
                                            .map((link) => (
                                                <tr key={link._id} className={link.isActive ? '' : 'table-secondary'}>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm"
                                                            value={link.displayOrder}
                                                            onChange={(e) => handleOrderChange(link._id, e.target.value)}
                                                            min="0"
                                                            style={{ width: '70px' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <i className={`${getPlatformIcon(link.platform, link.customIcon)} fa-lg mb-1`}></i>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                value={link.customIcon || ''}
                                                                onChange={(e) => handleCustomIconChange(link._id, e.target.value)}
                                                                placeholder="fab fa-..."
                                                                style={{ width: '120px', fontSize: '11px' }}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <span className="fw-bold">{getPlatformDisplayName(link.platform)}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="input-group input-group-sm">
                                                            <span className="input-group-text">
                                                                <i className="fas fa-link"></i>
                                                            </span>
                                                            <input
                                                                type="url"
                                                                className="form-control"
                                                                value={link.url}
                                                                onChange={(e) => handleUrlChange(link._id, e.target.value)}
                                                                placeholder={`Enter ${getPlatformDisplayName(link.platform)} URL`}
                                                            />
                                                        </div>
                                                        <small className="text-muted d-block mt-1">
                                                            Must start with https://
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <div className="form-check form-switch">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                role="switch"
                                                                checked={link.isActive}
                                                                onChange={(e) => handleActiveChange(link._id, e.target.checked)}
                                                                style={{ transform: 'scale(1.2)' }}
                                                            />
                                                            <label className="form-check-label">
                                                                {link.isActive ? (
                                                                    <span className="text-success">
                                                                        <i className="fas fa-check-circle me-1"></i>
                                                                        Active
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">
                                                                        <i className="fas fa-times-circle me-1"></i>
                                                                        Inactive
                                                                    </span>
                                                                )}
                                                            </label>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Preview Section */}
                    <div className="card mt-4">
                        <div className="card-header bg-white">
                            <h5 className="mb-0">
                                <i className="fas fa-eye me-2"></i>
                                Live Preview
                            </h5>
                            <p className="text-muted mb-0 small">How it will appear in the website header</p>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-lg-8">
                                    <div className="border rounded p-4 bg-dark mb-3">
                                        <div className="d-inline-flex align-items-center flex-wrap" style={{ minHeight: 45 }}>
                                            {socialLinks
                                                .filter(link => link.isActive)
                                                .sort((a, b) => a.displayOrder - b.displayOrder)
                                                .map((link) => (
                                                    <a
                                                        key={link._id}
                                                        className="btn btn-sm btn-outline-light btn-sm-square rounded-circle me-2 mb-2"
                                                        href={link.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title={getPlatformDisplayName(link.platform)}
                                                        style={{
                                                            transition: 'all 0.3s ease',
                                                            width: '36px',
                                                            height: '36px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        <i className={getPlatformIcon(link.platform, link.customIcon)}></i>
                                                    </a>
                                                ))
                                            }
                                            {socialLinks.filter(link => link.isActive).length === 0 && (
                                                <small className="text-light">
                                                    <i className="fas fa-info-circle me-1"></i>
                                                    No active social links. Activate some platforms above.
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-4">
                                    <div className="card border-primary">
                                        <div className="card-header bg-primary text-white">
                                            <h6 className="mb-0">Active Platforms Summary</h6>
                                        </div>
                                        <div className="card-body">
                                            {socialLinks
                                                .filter(link => link.isActive)
                                                .sort((a, b) => a.displayOrder - b.displayOrder)
                                                .map((link) => (
                                                    <div key={link._id} className="d-flex justify-content-between align-items-center mb-2">
                                                        <div>
                                                            <i className={getPlatformIcon(link.platform, link.customIcon) + ' me-2'}></i>
                                                            <span className="small">{getPlatformDisplayName(link.platform)}</span>
                                                        </div>
                                                        <small className="text-muted">#{link.displayOrder}</small>
                                                    </div>
                                                ))}
                                            {socialLinks.filter(link => link.isActive).length === 0 && (
                                                <div className="text-center text-muted py-2">
                                                    <i className="fas fa-exclamation-triangle me-1"></i>
                                                    No active platforms
                                                </div>
                                            )}
                                            <hr />
                                            <div className="d-flex justify-content-between small">
                                                <span className="text-muted">Total Platforms:</span>
                                                <span className="text-muted">{socialLinks.length}</span>
                                            </div>
                                            <div className="d-flex justify-content-between small">
                                                <span className="text-muted">Active:</span>
                                                <span className="text-success">{socialLinks.filter(link => link.isActive).length}</span>
                                            </div>
                                            <div className="d-flex justify-content-between small">
                                                <span className="text-muted">Inactive:</span>
                                                <span className="text-danger">{socialLinks.filter(link => !link.isActive).length}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-3">
                                <div className="alert alert-warning small">
                                    <i className="fas fa-lightbulb me-2"></i>
                                    <strong>Important:</strong>
                                    <ul className="mb-0 mt-1">
                                        <li>Changes are saved locally until you click "Save All Changes"</li>
                                        <li>Header will automatically update after saving</li>
                                        <li>Test URLs after saving to ensure they work correctly</li>
                                        <li>Contact info (address, phone, email) is managed separately in "Contact Info" tab</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default SocialLinksManager;